import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../../logging/logger.service';

/**
 * AuthGuard: protects routes by ensuring a valid session exists.
 * If no session is found, initiates SSO login (AzureAD) and blocks activation.
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private inProgress = false;
  constructor(
    private auth: AuthService,
    private router: Router,
    private logger: LoggerService,
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    try {
      const session = await firstValueFrom(
        this.auth.amplifyGetUserSession().pipe(
          catchError((err) => {
            this.logger.error('AuthGuard session error', err);
            return of(undefined);
          }),
        ),
      );
      // Narrow expected shape (partial typing to avoid explicit any)
      type PartialSession = { tokens?: { accessToken?: unknown } } | undefined;
      const s: PartialSession = session as PartialSession;
      const hasToken = !!s?.tokens?.accessToken;
      if (hasToken) {
        return true;
      }
    } catch (err) {
      this.logger.error('AuthGuard: exception while obtaining session', err);
    }
    if (!this.inProgress) {
      this.inProgress = true;
      this.logger.info('AuthGuard: no valid session; initiating SSO login');
      this.auth.loginSSO('AzureAD');
    }
    return false;
  }
}
